import { inject, Injectable, OnDestroy, signal, DOCUMENT } from '@angular/core';
import { finalize, map, Observable, Subject, take, tap } from 'rxjs';
import { UserService } from '../user/user-service';

@Injectable({
    providedIn: 'root',
})
export class SessionTimeoutService implements OnDestroy {
    private static readonly SESSION_TIMEOUT_MS = 30 * 60 * 1000;
    private static readonly WARNING_OFFSET_MS = 240 * 1000;
    private static readonly WARNING_TIMER_MS =
        SessionTimeoutService.SESSION_TIMEOUT_MS - SessionTimeoutService.WARNING_OFFSET_MS;
    private static readonly FINAL_COUNTDOWN_SEC = 180;

    private readonly _document: Document = inject(DOCUMENT);
    private readonly _userService: UserService = inject(UserService);
    private readonly _onVisibilityChange = (): void => {
        if (!this._document.hidden) {
            this._tickSessionTimer();
        }
    };
    private readonly _expired$ = new Subject<void>();

    private _sessionTimer!: NodeJS.Timeout;
    private _logoutDeadlineMs = 0;
    private _paused = false;

    readonly warningVisible = signal(false);
    readonly countdown = signal(SessionTimeoutService.FINAL_COUNTDOWN_SEC);
    readonly refreshLoading = signal(false);
    readonly expired$ = this._expired$.asObservable();

    constructor() {
        this._document.addEventListener('visibilitychange', this._onVisibilityChange);
    }

    start(): void {
        this.stop();

        if (this._paused) {
            return;
        }

        this._resetDeadline();
        this._tickSessionTimer();
        this._sessionTimer = setInterval(() => this._tickSessionTimer(), 1000);
    }

    resetOnActivity(): void {
        if (this._paused || !this._logoutDeadlineMs) {
            return;
        }

        this._resetDeadline();
    }

    stop(): void {
        clearInterval(this._sessionTimer);
        this._logoutDeadlineMs = 0;
        this.warningVisible.set(false);
        this.refreshLoading.set(false);
        this.countdown.set(SessionTimeoutService.FINAL_COUNTDOWN_SEC);
    }

    setPaused(paused: boolean): void {
        this._paused = paused;

        if (paused) {
            this.stop();
            return;
        }

        this.start();
    }

    staySignedIn(): Observable<void> {
        this.refreshLoading.set(true);

        return this._userService.whoAmI().pipe(
            take(1),
            tap(() => this.resetOnActivity()),
            finalize(() => this.refreshLoading.set(false)),
            map(() => void 0)
        );
    }

    ngOnDestroy(): void {
        this._document.removeEventListener('visibilitychange', this._onVisibilityChange);
        this.stop();
        this._expired$.complete();
    }

    private _resetDeadline(): void {
        this._logoutDeadlineMs =
            Date.now() + SessionTimeoutService.WARNING_TIMER_MS + SessionTimeoutService.FINAL_COUNTDOWN_SEC * 1000;
        this.warningVisible.set(false);
        this.countdown.set(SessionTimeoutService.FINAL_COUNTDOWN_SEC);
    }

    private _tickSessionTimer(): void {
        if (this._paused || !this._logoutDeadlineMs) {
            return;
        }

        const msRemaining = Math.max(0, this._logoutDeadlineMs - Date.now());

        if (msRemaining <= 0) {
            this.stop();
            this._expired$.next();
            return;
        }

        const warningMs = SessionTimeoutService.FINAL_COUNTDOWN_SEC * 1000;
        if (msRemaining <= warningMs) {
            this.warningVisible.set(true);
            this.countdown.set(Math.ceil(msRemaining / 1000));
        }
    }
}
